// Full updated file: EmailSidebarRecipientSelectionCard.tsx

'use client'

import { CircledEmptyCrossIcon } from '@repo/ui/CircledEmptyCrossIcon'
import { EnterIcon } from '@repo/ui/EnterIcon'
import { FunnelIcon } from '@repo/ui/FunnelIcon'
import { LoadingState } from '@repo/ui/LoadingState'
import { PeopleIcon } from '@repo/ui/PeopleIcon'
import { useDebounce } from '@uidotdev/usehooks'
import { useEffect, useState, useRef } from 'react'
import { useEmailCardStore } from '@repo/store/useEmailCardStore'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import MUIBadge from '@mui/material/Badge'
import MUIButton from '@mui/material/Button'
import MUIIconButton from '@mui/material/IconButton'
import MUIInput from '@mui/material/Input'
import MUIInputAdornment from '@mui/material/InputAdornment'
import MUIPopover from '@mui/material/Popover'
import MUIStack from '@mui/material/Stack'
import MUITab from '@mui/material/Tab'
import MUITabs from '@mui/material/Tabs'
import MUITypography from '@mui/material/Typography'
import styles from './EmailSidebarRecipientSelectionCard.module.scss'

type Recipient = {
  contactId?: string
  clientNo?: string
  organisationId?: string
  forename?: string
  surname?: string
  clientName?: string
  name?: string
  email?: string
  expertTypeId?: string
  emailAddress?: string
}

type RecipientType = 'clients' | 'contacts' | 'organisations'

type RecipientsResponse = {
  rows: Recipient[]
  totalRowCount: number
}

type EmailSidebarRecipientSelectionCardProps = {
  testData?: boolean
  noMatter?: boolean
}

export const EmailSidebarRecipientSelectionCard = ({
  noMatter,
  testData = false
}: EmailSidebarRecipientSelectionCardProps) => {
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null)
  const [filtered, setFiltered] = useState<Recipient[]>([])
  const [loading, setLoading] = useState(false)
  const [recipients, setRecipients] = useState<Recipient[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedExpertType, setSelectedExpertType] = useState<{ id: string; name: string } | null>(null)
  const [selectedValue, setSelectedValue] = useState<RecipientType>('clients')
  const [startAtIndex, setStartAtIndex] = useState(0)
  const [totalRowCount, setTotalRowCount] = useState(0)
  const debouncedSearchTerm = useDebounce(searchTerm, 500)
  const debouncedStartAtIndex = useDebounce(startAtIndex, 500)
  const pageSize = 25
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)

  const { fetchExpertTypes, expertTypesById } = useLookupsStore()
  const {
    bccRecipients,
    ccRecipients,
    fetchRecipients,
    removeBccRecipient,
    removeCcRecipient,
    removeToRecipient,
    setBccRecipients,
    setCcRecipients,
    setToRecipients,
    toRecipients
  } = useEmailCardStore()

  const counts: Record<RecipientType, number> = {
    clients:
      toRecipients.clients && ccRecipients.clients && bccRecipients.clients
        ? toRecipients.clients.length + ccRecipients.clients.length + bccRecipients.clients.length
        : 0,
    contacts:
      toRecipients.contacts && ccRecipients.contacts && bccRecipients.contacts
        ? toRecipients.contacts.length + ccRecipients.contacts.length + bccRecipients.contacts.length
        : 0,
    organisations:
      toRecipients.organisations && ccRecipients.organisations && bccRecipients.organisations
        ? toRecipients.organisations.length + ccRecipients.organisations.length + bccRecipients.organisations.length
        : 0
  }

  const setRecipientResponse = (res: RecipientsResponse) => {
    if (selectedValue === 'clients') {
      const rows = res || []

      // @ts-ignore
      setRecipients((prev) => (debouncedStartAtIndex === 0 ? rows : [...prev, ...rows]))
      // @ts-ignore
      setTotalRowCount(rows?.length || 0)
    } else {
      const rows = res?.rows || []
      setRecipients((prev) => (debouncedStartAtIndex === 0 ? rows : [...prev, ...rows]))
      setTotalRowCount(res?.totalRowCount || 0)
    }
  }

  useEffect(() => {
    if (testData) return
    const fetchNewData = async () => {
      setSelectedExpertType(null)
      setLoading(true)
      setSearchTerm('')
      setStartAtIndex(0)
      setRecipients([])
      setTotalRowCount(0)
      try {
        const res: RecipientsResponse = await fetchRecipients({
          recipientType: selectedValue,
          startAtIndex: 0,
          pageSize
        })

        setRecipientResponse(res)
      } catch {
        setRecipients([])
        setTotalRowCount(0)
      } finally {
        setLoading(false)
      }
    }

    !noMatter && fetchNewData()
  }, [fetchRecipients, noMatter, selectedValue, testData])

  useEffect(() => {
    if (testData) return
    fetchExpertTypes()
  }, [fetchExpertTypes, testData])

  useEffect(() => {
    if (testData) return
    const fetchData = async () => {
      setLoading(true)
      try {
        const res: RecipientsResponse = await fetchRecipients({
          recipientType: selectedValue,
          startAtIndex: debouncedStartAtIndex,
          pageSize,
          searchCriteria: debouncedSearchTerm || undefined
        })

        setRecipientResponse(res)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [fetchRecipients, testData, debouncedStartAtIndex])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 1 && recipients.length < totalRowCount && !loading) {
      setStartAtIndex((prev) => prev + pageSize)
    }
  }

  const handleSearch = async () => {
    if (testData) return
    if (!searchTerm) return
    try {
      setLoading(true)
      setStartAtIndex(0)
      listRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
      const res: RecipientsResponse = await fetchRecipients({
        recipientType: selectedValue,
        searchTerm,
        startAtIndex: 0,
        pageSize
      })

      setRecipientResponse(res)
    } catch (err) {
      console.error('Error fetching recipient search results:', err)
      setRecipients([])
      setTotalRowCount(0)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (testData || !recipients) return

    if (selectedValue === 'clients') {
      setFiltered(recipients.filter((r) => Boolean(r.emailAddress)))
    } else {
      setFiltered(
        recipients.filter((r) => {
          const id =
            selectedValue === 'contacts' ? r.contactId : selectedValue === 'organisations' ? r.organisationId : null
          return Boolean(id)
        })
      )
    }
  }, [recipients])

  return (
    <MUIStack
      direction="column"
      spacing={2}
      sx={{ pt: 2, minHeight: '412px' }}
      data-testid="EmailSidebarRecipientSelectionCard"
    >
      <MUITabs
        value={selectedValue}
        onChange={(_, v: RecipientType) => setSelectedValue(v)}
        className={styles.emailSidebarRecipientSelectionCardTabs}
        sx={{
          border: 'none',
          overflow: 'visible',
          minHeight: '28px',
          ['& .MuiTabs-scroller']: { overflow: 'visible' }
        }}
      >
        {(['clients', 'contacts', 'organisations'] as const).map((type) => (
          <MUITab
            key={type}
            value={type}
            label={
              <MUIBadge
                badgeContent={counts[type]}
                invisible={counts[type] === 0}
                color="secondary"
                sx={{
                  paddingRight: counts[type] ? '16px' : '0',
                  '.MuiBadge-badge': {
                    minWidth: '14px',
                    width: '14px',
                    height: '14px',
                    padding: 0,
                    top: '8px',
                    right: '4px',
                    color: selectedValue === type ? 'primary.main' : 'white',
                    backgroundColor: selectedValue === type ? 'white' : 'primary.main',
                    fontSize: '0.75rem'
                  },
                  '.MuiButtonBase-root': {
                    margin: '0 1%'
                  }
                }}
              >
                {type[0].toUpperCase() + type.slice(1)}
              </MUIBadge>
            }
            className={styles.emailSidebarRecipientSelectionCardTab}
            sx={{ overflow: 'hidden', marginRight: 0 }}
          />
        ))}
      </MUITabs>

      {selectedValue !== 'clients' && (
        <>
          <MUIStack direction="row" spacing={1} sx={{ mb: 2 }}>
            <MUIInput
              size="small"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                handleSearch()
              }}
              onKeyUp={(e) => e.key === 'Enter' && handleSearch()}
              inputProps={{ ref: searchInputRef, maxLength: 80 }}
              sx={{
                width: '100%',
                input: {
                  backgroundColor: 'background.dark',
                  height: '40px',
                  padding: '0 0 0 14px',
                  borderRadius: '6px'
                },
                '&:before, &:after': { content: 'none' }
              }}
              endAdornment={
                <MUIInputAdornment position="end" sx={{ position: 'absolute', right: 0 }}>
                  <MUIIconButton
                    onClick={handleSearch}
                    disabled={!searchTerm}
                    sx={{
                      borderRadius: '0 6px 6px 0',
                      height: '40px',
                      backgroundColor: searchTerm ? 'background.slate100' : 'transparent',
                      cursor: searchTerm ? 'pointer' : 'not-allowed'
                    }}
                  >
                    <MUITypography variant="button" sx={{ mr: 1, fontSize: 12 }}>
                      Search
                    </MUITypography>
                    <EnterIcon sx={{ width: 24, height: 24, color: searchTerm ? 'white' : 'grey' }} />
                  </MUIIconButton>
                </MUIInputAdornment>
              }
            />
            <MUIButton onClick={(e) => setFilterAnchorEl(e.currentTarget)} sx={{ minWidth: '44px' }}>
              <FunnelIcon sx={{ height: '16px', width: '16px' }} isSelected={!!selectedExpertType} />
            </MUIButton>
          </MUIStack>

          <MUIPopover
            open={Boolean(filterAnchorEl)}
            anchorEl={filterAnchorEl}
            onClose={() => setFilterAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <MUIStack
              sx={{ bgcolor: 'background.paper', p: 4, minWidth: 200, maxHeight: 300, overflowY: 'auto' }}
              spacing={4}
            >
              {expertTypesById &&
                typeof expertTypesById === 'object' &&
                Object.values(expertTypesById).map((type: any) => {
                  const isSelected = selectedExpertType?.id === type.expertTypeId
                  return (
                    <MUIButton
                      key={type.expertTypeId}
                      variant={isSelected ? 'contained' : 'outlined'}
                      color={isSelected ? 'primary' : 'inherit'}
                      onClick={async () => {
                        const newSelection = isSelected ? null : { id: type.expertTypeId, name: type.description }
                        setSelectedExpertType(newSelection)
                        setLoading(true)
                        setStartAtIndex(0)
                        try {
                          const res: RecipientsResponse = await fetchRecipients({
                            recipientType: selectedValue,
                            startAtIndex: 0,
                            pageSize,
                            typeId: newSelection?.id
                          })

                          setRecipientResponse(res)
                          listRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
                        } catch {
                          setRecipients([])
                          setTotalRowCount(0)
                        } finally {
                          setLoading(false)
                        }
                      }}
                      sx={{
                        justifyContent: 'flex-start',
                        fontSize: '0.875rem',
                        textTransform: 'capitalize',
                        borderRadius: 4,
                        border: 'none',
                        backgroundColor: isSelected ? 'primary.main' : 'rgba(61, 81, 123, 0.08)',
                        color: isSelected ? 'white' : 'inherit',
                        width: 'fit-content',
                        padding: '2px 14px'
                      }}
                    >
                      {type.description}
                    </MUIButton>
                  )
                })}
            </MUIStack>
          </MUIPopover>
        </>
      )}

      <MUIStack
        direction="column"
        gap={2}
        sx={{
          maxHeight: '20rem',
          minHeight: '20rem',
          overflowY: 'auto',
          overflowX: 'hidden',
          pb: 4,
          pr: '0.5rem',
          m: '8px -1rem 0 0 !important'
        }}
        onScroll={handleScroll}
        ref={listRef}
      >
        {filtered
          ? filtered.map((recipient, index) => {
              const getRecipientId = (r: Recipient) =>
                selectedValue === 'contacts' ? r.contactId : selectedValue === 'clients' ? r.name : r.organisationId

              const recipientId = getRecipientId(recipient)
              const emailAddr = selectedValue === 'clients' ? recipient.emailAddress : (recipient.email ?? '')

              if (!recipientId || !emailAddr) return null

              const isSameRecipient = (a: Recipient, b: Recipient, type: RecipientType) => {
                if (selectedValue === 'clients') {
                  return a.email === b.emailAddress
                } else {
                  return a.email === b.email
                }
              }

              const inTo = toRecipients?.[selectedValue]?.some((r: any) => isSameRecipient(r, recipient, selectedValue))
              const inCc = ccRecipients?.[selectedValue]?.some((r: any) => isSameRecipient(r, recipient, selectedValue))
              const inBcc = bccRecipients?.[selectedValue]?.some((r: any) =>
                isSameRecipient(r, recipient, selectedValue)
              )

              const displayName =
                selectedValue === 'contacts'
                  ? `${recipient.forename ?? ''} ${recipient.surname ?? ''}`.trim() || recipient.email || '—'
                  : selectedValue === 'clients'
                    ? (recipient.name ?? recipient.emailAddress ?? '—')
                    : (recipient.name ?? recipient.email ?? '—')

              const key = [selectedValue, recipientId, emailAddr, index].join('-')

              const expertType = recipient?.expertTypeId ? expertTypesById[recipient.expertTypeId] : ''
              const expertTypeDescription = expertType ? expertType.description : ''

              return (
                <MUIStack
                  key={key}
                  direction="row"
                  justifyContent="space-between"
                  gap={1}
                  sx={{
                    pt: 2,
                    pb: 2,
                    pr: 4,
                    pl: 1,
                    borderRadius: 1
                  }}
                >
                  <MUIStack direction="column" sx={{ flex: 1, maxWidth: 'calc(100% - 84px)' }}>
                    <MUITypography variant="body1" sx={{ fontWeight: 600, mb: 0 }} noWrap>
                      {displayName}
                      <span style={{ opacity: 0.5, fontSize: '0.75rem' }}>
                        {expertTypeDescription ? ` (${expertTypeDescription})` : ''}
                      </span>
                    </MUITypography>

                    {emailAddr && (
                      <MUITypography variant="body2" color="text.secondary" noWrap>
                        {emailAddr}
                      </MUITypography>
                    )}
                  </MUIStack>
                  <MUIStack direction="row" spacing={1} alignItems="center" sx={{ width: '80px' }}>
                    <MUIButton
                      variant={'contained'}
                      onClick={() => {
                        if (!inTo) {
                          setToRecipients({ recipientType: selectedValue, email: emailAddr, name: displayName })
                        } else {
                          removeToRecipient({ email: emailAddr })
                        }
                      }}
                      sx={{
                        borderRadius: '50%',
                        height: 24,
                        minWidth: 24,
                        p: 0,
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        backgroundColor: inTo ? 'secondary.main' : 'background.dark',
                        color: inTo ? 'white' : 'inherit',
                        '&:hover': { color: 'white', backgroundColor: 'secondary.light' }
                      }}
                    >
                      To
                    </MUIButton>
                    <MUIButton
                      variant={'contained'}
                      onClick={() => {
                        if (!inCc) {
                          setCcRecipients({ recipientType: selectedValue, email: emailAddr, name: displayName })
                        } else {
                          removeCcRecipient({ email: emailAddr })
                        }
                      }}
                      sx={{
                        borderRadius: '50%',
                        height: 24,
                        minWidth: 24,
                        p: 0,
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        backgroundColor: inCc ? 'secondary.main' : 'background.dark',
                        color: inCc ? 'white' : 'inherit',
                        '&:hover': { color: 'white', backgroundColor: 'secondary.light' }
                      }}
                    >
                      Cc
                    </MUIButton>
                    <MUIButton
                      variant={'contained'}
                      onClick={() => {
                        if (!inBcc) {
                          setBccRecipients({ recipientType: selectedValue, email: emailAddr, name: displayName })
                        } else {
                          removeBccRecipient({ email: emailAddr })
                        }
                      }}
                      sx={{
                        borderRadius: '50%',
                        height: 24,
                        minWidth: 24,
                        p: 0,
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        backgroundColor: inBcc ? 'secondary.main' : 'background.dark',
                        color: inBcc ? 'white' : 'inherit',
                        '&:hover': { color: 'white', backgroundColor: 'secondary.light' }
                      }}
                    >
                      Bcc
                    </MUIButton>
                  </MUIStack>
                </MUIStack>
              )
            })
          : null}

        {(!filtered || filtered.length === 0) && !loading ? (
          <MUIStack
            direction="column"
            alignItems="center"
            justifyContent="center"
            sx={{ py: 4, minHeight: selectedValue === 'clients' ? '19rem' : '16rem' }}
          >
            <PeopleIcon sx={{ width: '3rem', height: '3rem', mt: 6 }} />
            <MUITypography variant="body1" align="center" sx={{ py: 2 }}>
              No results found
            </MUITypography>
          </MUIStack>
        ) : null}

        {loading && (
          <MUIStack
            direction="row"
            justifyContent="center"
            alignItems="center"
            sx={{ height: '100%', width: '100%', minHeight: selectedValue === 'clients' ? '19rem' : '16rem' }}
          >
            <LoadingState />
          </MUIStack>
        )}
      </MUIStack>
    </MUIStack>
  )
}
